import React from 'react';

interface Props {}

// The heading here is the app name Google's OAuth reviewers compare against the
// consent screen, so it must read exactly "Fadila" and nothing else. The body
// has to name the services the app connects to and what it does with them - a
// homepage that only describes the idea in the abstract fails review for "does
// not explain the purpose of your app".

export default function Frontpage({}: Props) {
  return (
    <article className="grid-container">
      <h3>Fadila</h3>

      <p>
        Fadila is a web-based music player that plays the music you already own or follow, wherever
        it happens to live, from a single library and a single set of playlists.
      </p>

      <p>
        Music today is scattered. Some of it sits as audio files in a cloud drive, some of it is a
        music video in a YouTube playlist, some of it is a podcast episode. There is normally no way
        to put a track from one next to a track from another. Fadila does exactly that: one library,
        one queue, one player, across all of them.
      </p>

      <h4>What Fadila connects to</h4>
      <ul>
        <li>
          <strong>Google Drive</strong> — browse the audio files in your Drive, add them to
          playlists, and stream them to the player. Fadila reads your files to play them; it never
          modifies or deletes them.
        </li>
        <li>
          <strong>YouTube</strong> — read your own YouTube playlists so the music videos in them can
          be added to a Fadila playlist and played alongside your audio files.
        </li>
        <li>
          <strong>Microsoft OneDrive and Dropbox</strong> — alternatives to Google Drive, for audio
          files stored there instead.
        </li>
        <li>
          <strong>Podcasts</strong> — subscribe to any public RSS feed and play episodes from the
          same player.
        </li>
      </ul>

      <h4>Where your library is kept</h4>
      <p>
        Fadila has no server and no user accounts. Your library, playlists and settings are written
        as three small files into an app-specific folder in your own cloud drive — the same drive
        your music is in — so they follow you between devices without anyone else holding a copy.
        Your music is streamed straight from your drive to your browser and never passes through a
        server we run.
      </p>

      <p>
        Fadila's use of your Google, Microsoft, and Dropbox data is described in full in the{' '}
        <a href="/privacy">privacy policy</a>, and is limited to the playback and library features
        above. You can revoke Fadila's access at any time from the relevant provider's account
        permissions page.
      </p>
    </article>
  );
}
